import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Folder, HistoryScope, Mailbox } from '@mailpilot/shared';
import { api, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useFolders } from '../../hooks/useMailboxes';
import { Button, Checkbox, ChipInput, ErrorState, Field, Input, Segmented, SkeletonLines, useToast } from '../ui';
import { dateInputToIso, isoToDateInput } from './AccountsShared';
import { SubSection } from './SettingsPrimitives';

type FoldersRes = ResOf<'GET /api/mailboxes/:id/folders'>;

export function AccountsHistoryScope({ mb, onSave, saving }: { mb: Mailbox; onSave: (s: HistoryScope) => void; saving: boolean }) {
  const [scope, setScope] = useState<HistoryScope>(mb.historyScope);
  useEffect(() => setScope(mb.historyScope), [mb.historyScope]);
  const dirty = JSON.stringify(scope) !== JSON.stringify(mb.historyScope);
  const rangeInvalid = scope.mode === 'range' && !!scope.from && !!scope.to && scope.from > scope.to;

  return (
    <SubSection
      title="History scope"
      hint="How much past mail MailPilot imports and analyses. Imported history is never auto-replied to."
      actions={
        <Button variant="primary" size="sm" disabled={!dirty || rangeInvalid} loading={saving} onClick={() => onSave(scope)}>
          Save scope
        </Button>
      }
    >
      <div className="space-y-5">
        <Segmented
          label="History scope"
          value={scope.mode}
          onChange={(mode) => setScope((s) => ({ ...s, mode }))}
          options={[
            { value: 'full', label: 'Full history' },
            { value: 'range', label: 'Date range' },
          ]}
        />
        {scope.mode === 'range' && (
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="From" hint="Leave blank to start from the oldest message." error={rangeInvalid ? '“From” must be before “To”.' : undefined}>
              {(id) => <Input id={id} type="date" value={isoToDateInput(scope.from)} onChange={(e) => setScope((s) => ({ ...s, from: dateInputToIso(e.target.value) }))} />}
            </Field>
            <Field label="To" hint="Leave blank to include everything up to today.">
              {(id) => <Input id={id} type="date" value={isoToDateInput(scope.to)} onChange={(e) => setScope((s) => ({ ...s, to: dateInputToIso(e.target.value, true) }))} />}
            </Field>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <span className="text-[0.8125rem] font-medium text-ink">Excluded senders</span>
          <ChipInput
            label="Excluded senders"
            value={scope.excludedSenders}
            onChange={(excludedSenders) => setScope((s) => ({ ...s, excludedSenders }))}
            placeholder="name@example.com or @example.com, press Enter"
          />
          <p className="text-xs text-muted">Mail from these senders or domains is not imported.</p>
        </div>
        <FolderExclusions mailboxId={mb.id} />
      </div>
    </SubSection>
  );
}

function FolderExclusions({ mailboxId }: { mailboxId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useFolders(mailboxId);
  const toggle = useMutation({
    mutationFn: (f: { id: string; excluded: boolean }) => api('PATCH /api/folders/:id', { params: { id: f.id }, body: { excluded: f.excluded } }),
    onSuccess: (res) => {
      qc.setQueryData<FoldersRes>(qk.folders(mailboxId), (prev) => (prev ? { folders: prev.folders.map((f) => (f.id === res.folder.id ? res.folder : f)) } : prev));
    },
    onError: (e) => toast.error(e),
  });

  return (
    <div className="space-y-2">
      <p className="text-[0.8125rem] font-medium text-ink">Excluded folders</p>
      <p className="text-xs text-muted">Checked folders are skipped during import and sync. Changes apply immediately.</p>
      {q.isLoading ? (
        <SkeletonLines lines={3} />
      ) : q.isError ? (
        <ErrorState compact error={q.error} onRetry={() => void q.refetch()} title="Couldn't load folders" />
      ) : !q.data?.folders.length ? (
        <p className="rounded-xl bg-raised px-3.5 py-3 text-[0.8125rem] text-muted">No folders found yet. They appear after the first sync.</p>
      ) : (
        <div className="grid gap-x-6 gap-y-2.5 pt-1 sm:grid-cols-2">
          {q.data.folders.map((f: Folder) => (
            <Checkbox
              key={f.id}
              checked={f.excluded}
              disabled={toggle.isPending && toggle.variables?.id === f.id}
              onChange={(excluded) => toggle.mutate({ id: f.id, excluded })}
              label={`Exclude ${f.name}`}
              description={f.role !== 'custom' ? f.role : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
